const formatDateDisplay = (dateInput) => {
  if (!dateInput) return "";
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return dateInput;
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear().toString().slice(-2);
  return `${day}.${month}.${year}`;
};

const formatTimeDisplay = (timeStr) => {
  if (!timeStr) return null;
  if (timeStr.includes("AM") || timeStr.includes("PM")) return timeStr;
  const parts = timeStr.split(":");
  if (parts.length < 2) return timeStr;
  let hours = parseInt(parts[0], 10);
  const minutes = parts[1];
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  return `${hours}:${minutes} ${ampm}`;
};

export function transformApiData(apiData, selectedDate) {
  const transformed = {
    hitList: {
      id: "hitList",
      title: "HIT List",
      count: apiData["HIT List"]._meta.count,
      items: apiData["HIT List"].tasks.map((task) => ({
        id: `hit-${task.id}`,
        content: task.title,
        category: task.priority || "Task",
        date: formatDateDisplay(task.due_date),
        status: "HIT",
        indicator: "red",
        dataCount: task.sub_tasks ? task.sub_tasks.length : 0,
        hasSubtasks: task.sub_tasks && task.sub_tasks.length > 0,
        description: task.notes,
        sub_tasks: task.sub_tasks,
        source_type: task.source_type,
        start_time: formatTimeDisplay(task?.start_time),
        end_time: formatTimeDisplay(task?.end_time),
        time_duration: task?.remaining_time ?? null,
        is_reoccuring: task?.is_reoccuring,
      })),
    },
    mitList: {
      id: "mitList",
      title: "MIT List",
      count: `${apiData["Mit List"]._meta.count}/4`,
      progress: apiData["Mit List"]._meta.completed.percentage,
      items: [
        // MIT tasks (active)
        ...apiData["Mit List"].tasks.map((task) => ({
          id: `mit-${task.id}`,
          content: task.title,
          category: task.priority || "Task",
          date: formatDateDisplay(task.due_date),
          status: "MIT",
          indicator: "red",
          dataCount: task.sub_tasks ? task.sub_tasks.length : 0,
          hasSubtasks: task.sub_tasks && task.sub_tasks.length > 0,
          description: task.notes,
          sub_tasks: task.sub_tasks,
          source_type: task.source_type,
          start_time: formatTimeDisplay(task?.start_time),
          end_time: formatTimeDisplay(task?.end_time),
          time_duration: task?.remaining_time ?? null,
          is_reoccuring: task?.is_reoccuring,
        })),

        // Include achieved under MIT (gray items) - only for tasks whose due_date matches selectedDate
        ...apiData["Achieved"].tasks
          .filter((task) => {
            if (!task.due_date || !selectedDate) return false;
            const taskDueDate = new Date(task.due_date);
            const selectedDateObj = new Date(selectedDate);
            // Compare dates (year, month, day) ignoring time
            return (
              taskDueDate.getFullYear() === selectedDateObj.getFullYear() &&
              taskDueDate.getMonth() === selectedDateObj.getMonth() &&
              taskDueDate.getDate() === selectedDateObj.getDate()
            );
          })
          .map((task) => ({
            id: `mit-achieved-${task.id}`,
            content: task.title,
            category: task.priority || "Task",
            date: formatDateDisplay(task.due_date),
            status: "Achieved",
            indicator: "orange",
            dataCount: task.sub_tasks ? task.sub_tasks.length : 0,
            hasSubtasks: task.sub_tasks && task.sub_tasks.length > 0,
            description: task.notes,
            sub_tasks: task.sub_tasks,
            source_type: task.source_type,
          })),
      ],
    },
    achievedList: {
      id: "achievedList",
      title: "Achieved",
      count: `${apiData["Achieved"]._meta.completed.count}/20`,
      progress: apiData["Achieved"]._meta.completed.percentage,
      items: apiData["Achieved"].tasks.map((task) => ({
        id: `achieved-only-${task.id}`,
        content: task.title,
        category: task.priority || "Task",
        date: formatDateDisplay(task.due_date),
        status: "Acheived",
        indicator: "orange",
        dataCount: task.sub_tasks ? task.sub_tasks.length : 0,
        hasSubtasks: task.sub_tasks && task.sub_tasks.length > 0,
        description: task.notes,
        sub_tasks: task.sub_tasks,
        source_type: task.source_type,
        start_time: formatTimeDisplay(task?.start_time),
        end_time: formatTimeDisplay(task?.end_time),
        time_duration: task?.remaining_time ?? null,
        is_reoccuring: task?.is_reoccuring,
      })),
    },
    doList: {
      id: "doList",
      title: "Do List",
      count: `${apiData["Do List"]._meta.count}`,
      progress: apiData["Do List"]._meta.completed.percentage,
      items: [
        // DO tasks (active)
        ...apiData["Do List"].tasks.map((task) => ({
          id: `do-${task.id}`,
          content: task.title,
          category: task.priority || "Task",
          date: formatDateDisplay(task.due_date),
          status: "Do",
          indicator: "orange",
          dataCount: task.sub_tasks ? task.sub_tasks.length : 0,
          hasSubtasks: task.sub_tasks && task.sub_tasks.length > 0,
          description: task.notes,
          sub_tasks: task.sub_tasks,
          source_type: task.source_type,
          start_time: formatTimeDisplay(task?.start_time),
          end_time: formatTimeDisplay(task?.end_time),
          time_duration: task?.remaining_time ?? null,
          is_reoccuring: task?.is_reoccuring,
        })),

        // Include Done under DO (gray items) - only for tasks whose due_date matches selectedDate
        ...apiData["Done"].tasks
          .filter((task) => {
            if (!task.due_date || !selectedDate) return false;
            const taskDueDate = new Date(task.due_date);
            const selectedDateObj = new Date(selectedDate);
            // Compare dates (year, month, day) ignoring time
            return (
              taskDueDate.getFullYear() === selectedDateObj.getFullYear() &&
              taskDueDate.getMonth() === selectedDateObj.getMonth() &&
              taskDueDate.getDate() === selectedDateObj.getDate()
            );
          })
          .map((task) => ({
            id: `do-done-${task.id}`,
            content: task.title,
            category: task.priority || "Task",
            date: formatDateDisplay(task.due_date),
            status: "Done",
            indicator: "green",
            dataCount: task.sub_tasks ? task.sub_tasks.length : 0,
            hasSubtasks: task.sub_tasks && task.sub_tasks.length > 0,
            description: task.notes,
            sub_tasks: task.sub_tasks,
            source_type: task.source_type,
          })),
      ],
    },
    doneList: {
      id: "doneList",
      title: "Done",
      count: `${apiData["Done"]._meta.completed.count}`,
      progress: apiData["Done"]._meta.completed.percentage,
      items: apiData["Done"].tasks.map((task) => ({
        id: `done1-only-${task.id}`,
        content: task.title,
        category: task.priority || "Task",
        date: formatDateDisplay(task.due_date),
        status: "Done",
        indicator: "orange",
        dataCount: task.sub_tasks ? task.sub_tasks.length : 0,
        hasSubtasks: task.sub_tasks && task.sub_tasks.length > 0,
        description: task.notes,
        sub_tasks: task.sub_tasks,
        source_type: task.source_type,
        start_time: formatTimeDisplay(task?.start_time),
        end_time: formatTimeDisplay(task?.end_time),
        time_duration: task?.remaining_time ?? null,
        is_reoccuring: task?.is_reoccuring,
      })),
    },
    completedList: {
      id: "completedList",
      title: "Completed",
      doneItems: apiData["Done"].tasks.map((task) => ({
        id: `done-${task.id}`,
        content: task.title,
        category: task.priority || "Task",
        date: formatDateDisplay(task.due_date),
        status: "Done",
        indicator: "green",
        dataCount: task.sub_tasks ? task.sub_tasks.length : 0,
        done: true,
        description: task.notes,
        sub_tasks: task.sub_tasks,
        source_type: task.source_type,
        start_time: formatTimeDisplay(task?.start_time),
        end_time: formatTimeDisplay(task?.end_time),
        time_duration: task?.remaining_time ?? null,
        is_reoccuring: task?.is_reoccuring,
      })),
      achievedItems: apiData["Achieved"].tasks.map((task) => ({
        id: `ach-${task.id}`,
        content: task.title,
        category: task.priority || "Task",
        date: formatDateDisplay(task.due_date),
        status: "Achieved",
        indicator: "green",
        dataCount: task.sub_tasks ? task.sub_tasks.length : 0,
        done: true,
        description: task.notes,
        sub_tasks: task.sub_tasks,
        source_type: task.source_type,
        start_time: formatTimeDisplay(task?.start_time),
        end_time: formatTimeDisplay(task?.end_time),
        time_duration: task?.remaining_time ?? null,
        is_reoccuring: task?.is_reoccuring,
      })),
      doneCount: apiData["Done"]._meta.count,
      achievedCount: apiData["Achieved"]._meta.count,
    },
  };
  return transformed;
}

/**
 * Finds a task in the columns and returns the task and its source column ID
 * @param {string} taskId - The task ID to find
 * @param {object} columns - The columns object
 * @returns {object|null} - Object with sourceColumnId and task, or null if not found
 */
export function findTaskInColumns(taskId, columns) {
  for (const [colId, column] of Object.entries(columns)) {
    const foundTask = column?.items?.find((item) => item.id === taskId);
    if (foundTask) {
      return {
        sourceColumnId: colId,
        task: foundTask,
      };
    }
  }
  return null;
}

/**
 * Extracts the actual task ID from a formatted task ID
 * @param {string} taskId - The formatted task ID
 * @param {string} sourceColumnId - The source column ID
 * @returns {string} - The actual task ID
 */
export function extractActualTaskId(taskId, sourceColumnId) {
  if (sourceColumnId === "doneList" || sourceColumnId === "achievedList") {
    return taskId.split("only-")[1];
  } else if (taskId.startsWith("mit-achieved-")) {
    return taskId.split("mit-achieved-")[1];
  } else if (taskId.startsWith("do-done-")) {
    return taskId.split("do-done-")[1];
  } else {
    return taskId.split("-")[1];
  }
}

/**
 * Maps column ID to API level value
 * @param {string} columnId - The column ID
 * @param {string} showCompletedType - 'done' or 'achieved' for completedList
 * @returns {string} - The API level value
 */
export function getLevelFromColumnId(columnId, showCompletedType = "done") {
  const levelMap = {
    hitList: "HIT List",
    mitList: "MIT List",
    doList: "Do List",
    doneList: "Done",
    achievedList: "Achieved",
    completedList: showCompletedType === "done" ? "Done" : "Achieved",
  };
  return levelMap[columnId];
}

/**
 * Updates columns state for a task move (pure function)
 * @param {object} columns - Current columns state
 * @param {string} taskId - The task ID to move
 * @param {string} sourceColumnId - Source column ID
 * @param {string} destinationColumnId - Destination column ID
 * @param {object} task - The task object
 * @returns {object} - Updated columns state
 */
export function updateColumnsForTaskMove(
  columns,
  taskId,
  sourceColumnId,
  destinationColumnId,
  task,
) {
  // Don't move if same column
  if (sourceColumnId === destinationColumnId) {
    return columns;
  }

  // Create new columns state
  const newColumns = { ...columns };

  // Handle special cases for MIT/DO columns that now contain both active and completed tasks
  if (sourceColumnId === "mitList" && destinationColumnId === "achievedList") {
    // Moving from MIT to Achieved - update the task ID to greyed out format
    newColumns[sourceColumnId] = {
      ...newColumns[sourceColumnId],
      items: newColumns[sourceColumnId].items.map((item) =>
        item.id === taskId
          ? {
              ...item,
              id: `mit-achieved-${taskId.split("-")[1]}`,
              status: "Achieved",
            }
          : item,
      ),
    };
  } else if (
    sourceColumnId === "doList" &&
    destinationColumnId === "doneList"
  ) {
    // Moving from DO to Done - update the task ID to greyed out format
    newColumns[sourceColumnId] = {
      ...newColumns[sourceColumnId],
      items: newColumns[sourceColumnId].items.map((item) =>
        item.id === taskId
          ? {
              ...item,
              id: `do-done-${taskId.split("-")[1]}`,
              status: "Done",
            }
          : item,
      ),
    };
  } else {
    // Standard move - remove from source and add to destination
    newColumns[sourceColumnId] = {
      ...newColumns[sourceColumnId],
      items: newColumns[sourceColumnId].items.filter(
        (item) => item.id !== taskId,
      ),
    };

    newColumns[destinationColumnId] = {
      ...newColumns[destinationColumnId],
      items: [...newColumns[destinationColumnId].items, task],
    };
  }

  return newColumns;
}

/**
 * Main handler function for moving tasks
 * This function extracts all the necessary data for the component to handle the move
 * @param {string} taskId - The task ID to move
 * @param {string} destinationColumnId - The destination column ID
 * @param {object} columns - Current columns state
 * @param {string} showCompletedType - 'done' or 'achieved' for completedList
 * @returns {object} - Object containing all necessary data for the move operation
 */
export function handleMoveTask(
  taskId,
  destinationColumnId,
  columns,
  showCompletedType = "done",
) {
  // Find the task and determine the source column
  const taskInfo = findTaskInColumns(taskId, columns);

  if (!taskInfo) {
    return {
      success: false,
      error: "Error. The selected task was not found in the level.",
    };
  }

  const { sourceColumnId, task } = taskInfo;

  // Extract actual task ID for API call
  const actualTaskId = extractActualTaskId(taskId, sourceColumnId);

  // Map column ID to API level
  const newLevel = getLevelFromColumnId(destinationColumnId, showCompletedType);

  // Get updated columns state
  const updatedColumns = updateColumnsForTaskMove(
    columns,
    taskId,
    sourceColumnId,
    destinationColumnId,
    task,
  );

  return {
    success: true,
    sourceColumnId,
    destinationColumnId,
    task,
    actualTaskId,
    newLevel,
    updatedColumns,
    originalColumns: columns, // Keep original for rollback
  };
}

/**
 * Builds the request body for updating task level
 * Handles all the conditional logic for different column transitions
 * @param {string} taskId - The task ID
 * @param {string} newLevel - The new level value
 * @param {string} sourceColumnId - Source column ID
 * @param {string} destinationColumnId - Destination column ID
 * @param {string} formattedDate - Formatted date string (YYYY-MM-DD)
 * @returns {object} - Request body object for the API call
 */
export function buildTaskLevelUpdateRequestBody(
  taskId,
  newLevel,
  sourceColumnId,
  destinationColumnId,
  formattedDate,
) {
  // Default request body
  let requestBody = {
    level: newLevel,
    completion_status: "",
    scheduled_for: formattedDate,
    taskId: taskId,
    sourceColumnId: sourceColumnId,
    destinationColumnId: destinationColumnId,
  };

  // Handle moving from Completed/Achieved/Done back to active lists
  if (
    (destinationColumnId === "mitList" && sourceColumnId === "completedList") ||
    (destinationColumnId === "doList" && sourceColumnId === "completedList") ||
    (destinationColumnId === "mitList" && sourceColumnId === "achievedList") ||
    (destinationColumnId === "doList" && sourceColumnId === "doneList")
  ) {
    requestBody = {
      level: newLevel,
      completion_status: "",
      taskId: taskId,
      sourceColumnId: sourceColumnId,
      destinationColumnId: destinationColumnId,
    };
  }

  // Moving to Completed (either Done or Achieved)
  if (destinationColumnId === "completedList") {
    // Determine if it's coming from MIT (should be Achieved) or DO (should be Done)
    if (sourceColumnId === "mitList") {
      requestBody = {
        level: null,
        completion_status: "Achieved",
        taskId: taskId,
        sourceColumnId: sourceColumnId,
        destinationColumnId: destinationColumnId,
      };
    } else if (sourceColumnId === "doList") {
      requestBody = {
        level: null,
        completion_status: "Done",
        taskId: taskId,
        sourceColumnId: sourceColumnId,
        destinationColumnId: destinationColumnId,
      };
    }
  }

  // Moving from MIT to Achieved
  if (destinationColumnId === "achievedList" && sourceColumnId === "mitList") {
    requestBody = {
      level: null,
      completion_status: "Achieved",
      taskId: taskId,
      sourceColumnId: sourceColumnId,
      destinationColumnId: destinationColumnId,
    };
  }

  // Moving from DO to Done
  if (destinationColumnId === "doneList" && sourceColumnId === "doList") {
    requestBody = {
      level: null,
      completion_status: "Done",
      taskId: taskId,
      sourceColumnId: sourceColumnId,
      destinationColumnId: destinationColumnId,
    };
  }

  return requestBody;
}

/**
 * Validates if a task move from source column to destination column is allowed
 * Returns validation result with error message if invalid
 * @param {string} sourceColumnId - Source column ID
 * @param {string} destinationColumnId - Destination column ID
 * @param {string} showCompletedType - 'done' or 'achieved' for completedList
 * @returns {object} - Object with isValid (boolean) and errorMessage (string | null)
 */
export function validateTaskMove(
  sourceColumnId,
  destinationColumnId,
  showCompletedType = "done",
) {
  // Prevent moving from HIT List directly to Completed
  if (sourceColumnId === "hitList" && destinationColumnId === "completedList") {
    return {
      isValid: false,
      errorMessage: "Cannot move tasks directly from HIT List to Completed",
    };
  }

  // Prevent moving between MIT and DO
  if (
    (sourceColumnId === "mitList" && destinationColumnId === "doList") ||
    (sourceColumnId === "doList" && destinationColumnId === "mitList")
  ) {
    return {
      isValid: false,
      errorMessage: "Cannot move tasks directly between MIT List and Do List",
    };
  }

  // Allow moving from Completed (Done) to Do List
  if (
    sourceColumnId === "completedList" &&
    destinationColumnId !== "completedList" &&
    showCompletedType === "done" &&
    destinationColumnId === "doList"
  ) {
    return { isValid: true, errorMessage: null };
  }

  // Allow moving from Completed (Achieved) to MIT List
  if (
    sourceColumnId === "completedList" &&
    destinationColumnId !== "completedList" &&
    showCompletedType === "achieved" &&
    destinationColumnId === "mitList"
  ) {
    return { isValid: true, errorMessage: null };
  }

  // Prevent moving Achieved tasks to lists other than MIT
  if (
    sourceColumnId === "completedList" &&
    showCompletedType === "achieved" &&
    destinationColumnId !== "mitList"
  ) {
    return {
      isValid: false,
      errorMessage:
        "Achieved tasks cannot be moved to lists other than MIT List.",
    };
  }

  // Prevent moving Done tasks to lists other than Do List
  if (
    sourceColumnId === "completedList" &&
    showCompletedType === "done" &&
    destinationColumnId !== "doList"
  ) {
    return {
      isValid: false,
      errorMessage: "Done tasks cannot be moved to lists other than Do List.",
    };
  }

  // Prevent moving from MIT List to Done List
  if (sourceColumnId === "mitList" && destinationColumnId === "doneList") {
    return {
      isValid: false,
      errorMessage: "Cannot move tasks from MIT List to Done List",
    };
  }

  // Prevent moving from Do List to Achieved List
  if (sourceColumnId === "doList" && destinationColumnId === "achievedList") {
    return {
      isValid: false,
      errorMessage: "Cannot move tasks from Do List to Achieved List",
    };
  }

  // Allow moving from MIT to Completed, HIT, or Achieved
  if (sourceColumnId === "mitList") {
    const allowedDestinations = ["completedList", "hitList", "achievedList"];
    if (allowedDestinations.includes(destinationColumnId)) {
      return { isValid: true, errorMessage: null };
    }
  }

  // Allow moving from DO to Completed, HIT, or Done
  if (sourceColumnId === "doList") {
    const allowedDestinations = ["completedList", "hitList", "doneList"];
    if (allowedDestinations.includes(destinationColumnId)) {
      return { isValid: true, errorMessage: null };
    }
  }

  // Allow moving from HIT to MIT or DO
  if (sourceColumnId === "hitList") {
    const allowedDestinations = ["mitList", "doList"];
    if (allowedDestinations.includes(destinationColumnId)) {
      return { isValid: true, errorMessage: null };
    }
  }

  // Allow reordering within the same column
  if (sourceColumnId === destinationColumnId) {
    return { isValid: true, errorMessage: null };
  }

  // Prevent moving from Achieved to lists other than MIT
  if (sourceColumnId === "achievedList" && destinationColumnId !== "mitList") {
    return {
      isValid: false,
      errorMessage:
        "Achieved tasks cannot be moved to lists other than MIT List",
    };
  }

  // Prevent moving from Done to lists other than Do List
  if (sourceColumnId === "doneList" && destinationColumnId !== "doList") {
    return {
      isValid: false,
      errorMessage: "Done tasks cannot be moved to lists other than Do List",
    };
  }

  // Allow moving from Achieved to MIT
  if (sourceColumnId === "achievedList") {
    return destinationColumnId === "mitList"
      ? { isValid: true, errorMessage: null }
      : { isValid: false, errorMessage: null };
  }

  // Allow moving from Done to Do List
  if (sourceColumnId === "doneList") {
    return destinationColumnId === "doList"
      ? { isValid: true, errorMessage: null }
      : { isValid: false, errorMessage: null };
  }

  // Default: invalid move
  return { isValid: false, errorMessage: null };
}

/**
 * Finds the dragged item from the source column
 * Handles special case for completedList which has doneItems/achievedItems
 * @param {object} startColumn - The source column
 * @param {string} sourceColumnId - Source column ID
 * @param {string} draggableId - The ID of the dragged item
 * @param {string} showCompletedType - 'done' or 'achieved' for completedList
 * @returns {object|null} - The found item or null
 */
export function findDraggedItem(
  startColumn,
  sourceColumnId,
  draggableId,
  showCompletedType = "done",
) {
  if (sourceColumnId === "completedList") {
    const sourceItems =
      showCompletedType === "done"
        ? startColumn.doneItems
        : startColumn.achievedItems;
    return sourceItems?.find((item) => item.id === draggableId) || null;
  } else {
    return startColumn.items?.find((item) => item.id === draggableId) || null;
  }
}

/**
 * Reorders items within the same column
 * Handles special case for completedList which has doneItems/achievedItems
 * @param {object} columns - Current columns state
 * @param {string} columnId - Column ID
 * @param {number} sourceIndex - Source index
 * @param {number} destinationIndex - Destination index
 * @param {string} showCompletedType - 'done' or 'achieved' for completedList
 * @returns {object} - Updated columns state
 */
export function reorderItemsInColumn(
  columns,
  columnId,
  sourceIndex,
  destinationIndex,
  showCompletedType = "done",
) {
  const column = columns[columnId];
  if (!column) return columns;

  if (columnId === "completedList") {
    const sourceItemsKey =
      showCompletedType === "done" ? "doneItems" : "achievedItems";
    const newItems = Array.from(column[sourceItemsKey] || []);
    const [removed] = newItems.splice(sourceIndex, 1);
    newItems.splice(destinationIndex, 0, removed);

    return {
      ...columns,
      [columnId]: {
        ...column,
        [sourceItemsKey]: newItems,
      },
    };
  } else {
    const newItems = Array.from(column.items || []);
    const [removed] = newItems.splice(sourceIndex, 1);
    newItems.splice(destinationIndex, 0, removed);

    return {
      ...columns,
      [columnId]: {
        ...column,
        items: newItems,
      },
    };
  }
}

/**
 * Updates columns state for drag and drop between different columns
 * Handles all special cases for MIT/Achieved and DO/Done transitions
 * @param {object} columns - Current columns state
 * @param {string} sourceColumnId - Source column ID
 * @param {string} destinationColumnId - Destination column ID
 * @param {string} draggableId - The ID of the dragged item
 * @param {object} item - The dragged item object
 * @param {string} showCompletedType - 'done' or 'achieved' for completedList
 * @returns {object} - Updated columns state
 */
export function updateColumnsForDragDrop(
  columns,
  sourceColumnId,
  destinationColumnId,
  draggableId,
  item,
  showCompletedType = "done",
) {
  const start = columns[sourceColumnId];
  const finish = columns[destinationColumnId];

  if (!start || !finish) return columns;

  // Handle source column update
  let newStart;
  if (sourceColumnId === "completedList") {
    const sourceItemsKey =
      showCompletedType === "done" ? "doneItems" : "achievedItems";
    const newItems = Array.from(start[sourceItemsKey] || []).filter(
      (i) => i.id !== draggableId,
    );
    newStart = {
      ...start,
      [sourceItemsKey]: newItems,
    };
  } else if (
    sourceColumnId === "mitList" &&
    destinationColumnId === "achievedList"
  ) {
    // Moving from MIT to Achieved - update ID and status in MIT column
    const newItems = Array.from(start.items || []).map((i) =>
      i.id === draggableId
        ? {
            ...i,
            id: `mit-achieved-${draggableId.split("-")[1]}`,
            status: "Achieved",
          }
        : i,
    );
    newStart = {
      ...start,
      items: newItems,
    };
  } else if (
    sourceColumnId === "doList" &&
    destinationColumnId === "doneList"
  ) {
    // Moving from DO to Done - update ID and status in DO column
    const newItems = Array.from(start.items || []).map((i) =>
      i.id === draggableId
        ? {
            ...i,
            id: `do-done-${draggableId.split("-")[1]}`,
            status: "Done",
          }
        : i,
    );
    newStart = {
      ...start,
      items: newItems,
    };
  } else {
    // Standard move - remove from source column
    const newItems = Array.from(start.items || []).filter(
      (i) => i.id !== draggableId,
    );
    newStart = {
      ...start,
      items: newItems,
    };
  }

  // Handle destination column update
  let newFinish;
  if (destinationColumnId === "completedList") {
    const destItemsKey =
      showCompletedType === "done" ? "doneItems" : "achievedItems";
    const newItems = [...(finish[destItemsKey] || []), item];
    newFinish = {
      ...finish,
      [destItemsKey]: newItems,
    };
  } else if (
    sourceColumnId === "mitList" &&
    destinationColumnId === "achievedList"
  ) {
    // Don't add to achieved column since it's already in MIT column (greyed out)
    newFinish = finish;
  } else if (
    sourceColumnId === "doList" &&
    destinationColumnId === "doneList"
  ) {
    // Don't add to done column since it's already in DO column (greyed out)
    newFinish = finish;
  } else {
    const newItems = [...(finish.items || []), item];
    newFinish = {
      ...finish,
      items: newItems,
    };
  }

  return {
    ...columns,
    [newStart.id]: newStart,
    [newFinish.id]: newFinish,
  };
}
